import EN from '../../../../EN.json';
import FR from '../../../../FR.json';

/**
 * Canonical value normalizers for the Other Offers filtering/matching flow (FR-006b).
 *
 * Matching for Term, Bundled and TSB Plan MUST use canonical, locale-independent
 * values. Localized English/French labels are used only for rendering in the UI.
 * Each helper accepts either a canonical value OR a localized EN/FR label and
 * returns the canonical token. The canonical<->label maps are derived from the
 * existing EN.json / FR.json keys so there is a single source of truth.
 */
const enVul = EN.vulcanWebform || {};
const frVul = FR.vulcanWebform || {};

const TERM_CANONICAL = ['Month to Month', '36 Months', '60 Months'];
const PLAN_CANONICAL = ['Monitor', 'Protect', 'Smart Camera'];

/**
 * Normalize a Term value to its canonical English token.
 * @param {string} input - canonical value or localized label (e.g. 'Durée de 36 mois')
 * @returns {string} canonical token (e.g. '36 Months') or the original input if unknown
 */
export function normalizeTerm(input) {
  if (input == null) return input;
  const value = String(input).trim();
  if (TERM_CANONICAL.includes(value)) return value;
  const labelToCanonical = {
    [enVul.offerTermMonthToMonth]: 'Month to Month',
    [frVul.offerTermMonthToMonth]: 'Month to Month',
    [enVul.vulThirtyMonth]: '36 Months',
    [frVul.vulThirtyMonth]: '36 Months',
    [enVul.vulSixtyMonth]: '60 Months',
    [frVul.vulSixtyMonth]: '60 Months'
  };
  return labelToCanonical[value] || value;
}

/**
 * Normalize a Bundled value to a canonical boolean.
 * @param {string|boolean} input - boolean, 'Yes'/'No', or localized 'Oui'/'Non'
 * @returns {boolean} canonical boolean, or the original input if unknown
 */
export function normalizeBundled(input) {
  if (typeof input === 'boolean') return input;
  if (input == null) return input;
  const value = String(input).trim().toLowerCase();
  const yesSet = new Set(
    ['yes', 'true', enVul.yes, frVul.yes].filter(Boolean).map(v => String(v).toLowerCase())
  );
  const noSet = new Set(
    ['no', 'false', enVul.no, frVul.no].filter(Boolean).map(v => String(v).toLowerCase())
  );
  if (yesSet.has(value)) return true;
  if (noSet.has(value)) return false;
  return input;
}

/**
 * Normalize a TSB plan value to its canonical English token.
 * @param {string} input - canonical value or localized label (e.g. 'Surveillance')
 * @returns {string} canonical token (e.g. 'Monitor') or the original input if unknown
 */
export function normalizePlan(input) {
  if (input == null) return input;
  const value = String(input).trim();
  if (PLAN_CANONICAL.includes(value)) return value;
  const labelToCanonical = {
    [enVul.vulMonitorTSB]: 'Monitor',
    [frVul.vulMonitorTSB]: 'Monitor',
    [enVul.vulProtectTSB]: 'Protect',
    [frVul.vulProtectTSB]: 'Protect',
    [enVul.vulSmartCameraTSB]: 'Smart Camera',
    [frVul.vulSmartCameraTSB]: 'Smart Camera'
  };
  return labelToCanonical[value] || value;
}

/**
 * Whether a TSB offer applies to the selected plan. Offers with no tsbPlan never apply;
 * with no plan selected yet, every plan-tagged offer applies.
 */
export function isOfferForTsbPlan(offer, selectedPlan) {
  const plan = offer && offer.tsbPlan;
  if (!plan || (Array.isArray(plan) && plan.length === 0)) return false;
  const selected = normalizePlan(selectedPlan);
  if (!selected || !Array.isArray(plan)) return true;
  return plan.map(normalizePlan).includes(selected);
}

/**
 * Finds the first selected row that requires a validation code but has none entered.
 * @param {Array} rows - Selected rows
 * @returns {{ offerCode: string, message: string } | null} - The offer and error message if missing, else null
 */
export function getMissingValidationOffer(rows,locale) {
  if (!rows || rows.length === 0) return null;
  const missing = rows.find(
    r =>
      r.validationCode === 'Yes' &&
      (!r.enteredValidationCode || !r.enteredValidationCode.trim())
  );
  if (!missing) return null;
  return {
    offerCode: missing.offerCode,
    message: locale === 'en-US'
      ? `Validation code for offer ${missing.offerCode} is required.`
      : ` Le code de validation de l'offre ${missing.offerCode} est requis.`
  };
}

export function calculateCharges(data = []) {
  let monthlyChargesPrice = 0;
  let oneTimeChargesPrice = 0;

  data.forEach(item => {
    (item.offerDetails || []).forEach(detail => {
      const val = Number(detail.value) || 0;
      if (detail.frequency === 'monthly') monthlyChargesPrice += val;
      else if (detail.frequency === 'oneTime') oneTimeChargesPrice += val;
    });
  });

  return { monthlyChargesPrice, oneTimeChargesPrice };
}

export function prepareCouponData(selectedRows, filters) {
  // add validationCode to each row
  const couponData = selectedRows.map(item => {
    // Offer targeting data isn't part of the order
    const { channels, dealerships, allChannels, allDealerships, ...rest } = item;

    return {
      ...rest
    };
  });

  // calculate totals using your helper
  const { monthlyChargesPrice, oneTimeChargesPrice } = calculateCharges(
    couponData
  );

  return {
    data: couponData,
    monthlyCharges: monthlyChargesPrice,
    oneTimeCharges: oneTimeChargesPrice,
    ...filters
  };
}

/**
 * Drops already-applied TSB coupons that are not for the selected plan and recomputes the
 * totals. Returns the same object when nothing is removed.
 */
export function removeCouponsNotForTsbPlan(tsbData, selectedPlan) {
  if (!normalizePlan(selectedPlan)) return tsbData;
  const rows = tsbData && Array.isArray(tsbData.data) ? tsbData.data : [];
  const kept = rows.filter(offer => isOfferForTsbPlan(offer, selectedPlan));
  if (kept.length === rows.length) return tsbData;
  const { monthlyChargesPrice, oneTimeChargesPrice } = calculateCharges(kept);
  return {
    ...tsbData,
    data: kept,
    monthlyCharges: monthlyChargesPrice,
    oneTimeCharges: oneTimeChargesPrice
  };
}

// Shared styles for all Selects in the filter
 export const selectStyles = {
    container: base => ({
      ...base,
      width: '250px',
      minWidth: '220px',
      maxWidth: '250px'
    }),
    menuList: base => ({
      ...base,
      maxHeight: '200px' // scrollable dropdown if too many options
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
  };